import { proxy } from "valtio";
import type { PlacementOption } from "@models/PlacementOption.model";
import type { PlacementPair } from "@services/placements/commands/cmdSetAudienceAdPlacements";

// assignments[adGroupAdId] = PlacementPair[] (the placements assigned to this ad-group-ad)
export type AssignmentMap = Record<number, PlacementPair[]>;

// Catalog grouped by network for easy lookup
export type GroupedCatalog = Record<string, { label: string; placements: PlacementOption[] }>;

export const publishStore = proxy<{
  selectedAudienceId: number | null;
  assignments: AssignmentMap;
  catalog: PlacementOption[];

  selectedAdIds: number[];
  visibleNetworks: string[];
  adSearchQuery: string;

  activePopover: { adGroupAdId: number; network: string } | null;

  isLoading: boolean;
}>({
  selectedAudienceId: null,
  assignments: {},
  catalog: [],

  selectedAdIds: [],
  visibleNetworks: [],
  adSearchQuery: "",

  activePopover: null,

  isLoading: false,
});

export const publishActions = {
  setSelectedAudience(audienceId: number | null) {
    publishStore.selectedAudienceId = audienceId;
    publishStore.assignments = {};
    publishStore.selectedAdIds = [];
    publishStore.activePopover = null;
  },

  setAssignments(assignments: AssignmentMap) {
    publishStore.assignments = assignments;
  },

  setCatalog(catalog: PlacementOption[]) {
    publishStore.catalog = catalog;
  },

  updateAssignment(adGroupAdId: number, placements: PlacementPair[]) {
    publishStore.assignments[adGroupAdId] = placements;
  },

  toggleAdSelection(adId: number) {
    const idx = publishStore.selectedAdIds.indexOf(adId);
    if (idx >= 0) {
      publishStore.selectedAdIds.splice(idx, 1);
    } else {
      publishStore.selectedAdIds.push(adId);
    }
  },

  selectAllAds(adIds: number[]) {
    publishStore.selectedAdIds = [...adIds];
  },

  clearAdSelection() {
    publishStore.selectedAdIds = [];
  },

  setActivePopover(popover: { adGroupAdId: number; network: string } | null) {
    publishStore.activePopover = popover;
  },

  setVisibleNetworks(networks: string[]) {
    publishStore.visibleNetworks = networks;
  },

  setAdSearchQuery(query: string) {
    publishStore.adSearchQuery = query;
  },
};

/** Group the flat catalog by network */
export function groupCatalog(catalog: PlacementOption[]): GroupedCatalog {
  const grouped: GroupedCatalog = {};
  for (const opt of catalog) {
    if (!grouped[opt.network]) {
      grouped[opt.network] = { label: opt.network_label, placements: [] };
    }
    grouped[opt.network].placements.push(opt);
  }
  return grouped;
}

/** Get placements for a specific network from an ad-group-ad's assignment */
export function getNetworkPlacements(assignments: PlacementPair[], network: string): string[] {
  return assignments.filter((p) => p.network === network).map((p) => p.placement_type);
}

/** Replace placements for one network, keeping other networks intact */
export function replaceNetworkPlacements(
  current: PlacementPair[],
  network: string,
  newTypes: string[]
): PlacementPair[] {
  const otherNetworks = current.filter((p) => p.network !== network);
  const newPairs = newTypes.map((t) => ({ network, placement_type: t }));
  return [...otherNetworks, ...newPairs];
}
